import React, { useState } from 'react';
import { MenuDownIcon } from '@mc/wink-icons';
import { ChevronDown } from '@design-systems/icons';
import cx from 'classnames';
import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';
import Animate from '../Animate';
import stylesheet from './Accordion.css';

export type AccordionItemProps = {
  /** Content inside the accordion */
  children: React.ReactNode;
  /** When true, the accordion item will be inititally expanded */
  defaultIsExpanded?: boolean;
  /** Makes the Accordion unclickable */
  disabled?: boolean;
  /** The text shown in the toggle button  */
  heading: string | React.ReactNode;
  /** A callback that triggers when the accordion expands/collapses. */
  onToggle?: (isExpanded: boolean) => void;
  /** Sets the position of the ChevronDown icon */
  position?: 'left' | 'right';
  /** Only used when `position` is set to `right` */
  prefixIcon?: React.ReactNode;
  /** Sets the visual style of the Accordion*/
  type?: 'card' | 'container' | 'default';
};

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  function AccordionItem(
    {
      children,
      heading,
      onToggle = () => {},
      defaultIsExpanded = false,
      disabled = false,
      position = 'right',
      prefixIcon,
      type = 'card',
    },
    forwardedRef,
  ) {
    const [isExpanded, setIsExpanded] = useState(defaultIsExpanded);
    const redesign = mcdsFlagCheck('xp_mcds_redesign_components_organisms');

    return (
      <div
        className={cx(
          stylesheet.item,
          redesign && stylesheet[type],
          redesign && disabled && stylesheet.disabled,
        )}
        ref={forwardedRef}
      >
        <button
          aria-expanded={isExpanded}
          type="button"
          className={cx(
            stylesheet.heading,
            redesign &&
              (position === 'left'
                ? stylesheet.iconLeft
                : stylesheet.iconRight),
          )}
          onClick={() => {
            if (!disabled) {
              onToggle(!isExpanded);
              setIsExpanded((value) => !value);
            }
          }}
        >
          {redesign ? (
            <>
              {position === 'left' ? (
                <>
                  <ChevronDown />
                  {heading}
                </>
              ) : (
                <>
                  <span className={stylesheet.prefixIcon}>{prefixIcon} </span>
                  {heading}
                  <ChevronDown />
                </>
              )}
            </>
          ) : (
            <>
              <MenuDownIcon />
              {heading}
            </>
          )}
        </button>
        <Animate
          animation="slide"
          easing="expressive"
          toggle={isExpanded}
          className={
            redesign
              ? position === 'left'
                ? stylesheet.bodyLeft
                : stylesheet.bodyRight
              : stylesheet.body
          }
        >
          {children}
        </Animate>
      </div>
    );
  },
);

export type AccordionProps = {
  /** Pass `AccordionItem` components */
  children?: React.ReactNode;
};

const Accordion = React.forwardRef<HTMLUListElement, AccordionProps>(
  function Accordion({ children }, forwardedRef) {
    return (
      // We explicitly set role="list" to accommodate VoiceOver, which hides list
      // semantics when `list-style` is reset.
      // eslint-disable-next-line jsx-a11y/no-redundant-roles
      <ul role="list" className={stylesheet.root} ref={forwardedRef}>
        {React.Children.map(children, (child) => {
          return child && <li className={stylesheet.listItem}>{child}</li>;
        })}
      </ul>
    );
  },
);

export default Accordion;
export { AccordionItem };
